<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { dayMonthLabel, messageClockTime } from 'shared/helpers/timeHelper';
import maintenanceNoticeAPI from 'dashboard/api/maintenanceNotice';
import Banner from 'dashboard/components-next/banner/Banner.vue';

// Aviso de manutencao da instalacao (AVISO_MANUTENCAO, Super Admin): vale para todos os usuarios.
const ADVANCE_NOTICE_MS = 24 * 60 * 60 * 1000;
const CLOCK_TICK_MS = 30 * 1000;
// A aba do painel fica aberta o dia todo: um aviso configurado depois do carregamento chega por aqui.
const REFRESH_NOTICE_MS = 5 * 60 * 1000;

const { t, locale } = useI18n();
const globalConfig = useMapGetter('globalConfig/get');

const now = ref(Date.now());
const dismissedStart = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.DISMISSED_MAINTENANCE_NOTICE)
);
let clock = null;
let refresher = null;

const fetchedNotice = ref(undefined);
// Vale o do carregamento da pagina ate a primeira consulta; depois, o ultimo que o servidor devolveu.
const notice = computed(() =>
  fetchedNotice.value === undefined
    ? globalConfig.value?.avisoManutencao
    : fetchedNotice.value
);
const startMs = computed(() => Date.parse(notice.value?.inicio));
const endMs = computed(
  () => startMs.value + (notice.value?.duracao_minutos || 0) * 60 * 1000
);

const isVisible = computed(() => {
  if (!notice.value || Number.isNaN(startMs.value)) return false;
  if (dismissedStart.value === notice.value.inicio) return false;
  return (
    now.value >= startMs.value - ADVANCE_NOTICE_MS && now.value < endMs.value
  );
});

const message = computed(() => {
  if (notice.value.mensagem) return notice.value.mensagem;
  const startSeconds = startMs.value / 1000;
  const minutes = notice.value.duracao_minutos;
  return t(
    'GENERAL_SETTINGS.MAINTENANCE_NOTICE.MESSAGE',
    {
      date: dayMonthLabel(startSeconds, locale.value),
      time: messageClockTime(startSeconds, locale.value),
      duration: minutes,
    },
    minutes
  );
});

// A chave do "fechar" e o inicio: se o horario mudar, o aviso volta.
const dismiss = () => {
  dismissedStart.value = notice.value.inicio;
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.DISMISSED_MAINTENANCE_NOTICE,
    dismissedStart.value
  );
};

// Erro de rede: mantem o ultimo valor.
const refreshNotice = async () => {
  try {
    const { data } = await maintenanceNoticeAPI.get();
    fetchedNotice.value = data?.inicio ? data : null;
  } catch {
    // sem rede agora; a proxima consulta tenta de novo
  }
};

onMounted(() => {
  refresher = setInterval(refreshNotice, REFRESH_NOTICE_MS);
  clock = setInterval(() => {
    now.value = Date.now();
  }, CLOCK_TICK_MS);
});

onBeforeUnmount(() => {
  clearInterval(clock);
  clearInterval(refresher);
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Banner
    v-if="isVisible"
    color="amber"
    :action-label="t('GENERAL_SETTINGS.DISMISS')"
    class="!rounded-none !justify-center"
    @action="dismiss"
  >
    <span class="text-xs">{{ message }}</span>
  </Banner>
</template>
